import {
  BookingItemPropertyType,
  BookingItemType,
  BookingSource,
  DateTime,
  IntegrationType,
} from "enums";
import { eventBookingDateTimeRequest, filterRequest, selectToObjectRequest } from "mappers";
// import moment from "moment";
import { formatDate } from "services";
import { getNightsCount } from "services/date.service";
import { gender2str, identificationType2str } from "services/text.service";
import { isEatappSource } from "services/booking.service";
import { isEmpty } from "lodash";

export const bookingDetailsResponse = {
  bookingNumber: { key: "bookingNumber" },
  bookingId: { key: "bookingId" },
  startTime: {
    transform: ({ src }) => {
      if (src.source === IntegrationType.CLOUDBEDS) {
        return formatDate(src.startTime, DateTime.A);
      }
      return formatDate(src.startTime, DateTime.H);
    },
  },
  arrivalDate: {
    transform: ({ src }) => {
      return formatDate(src.startTime, DateTime.A);
    },
  },
  arrivalTime: {
    transform: ({ src }) => {
      return formatDate(src.startTime, DateTime.D);
    },
  },
  endTime: {
    transform: ({ src }) => {
      if (src.source === IntegrationType.CLOUDBEDS) {
        return formatDate(src.endTime.split("T")[0], DateTime.A);
      }
      return formatDate(src.endTime, DateTime.H);
    },
  },
  bookedAt: { key: "bookedAt" },
  bookingItems: {
    transform: ({ src }) => {
      return src.bookingItems.sort((a, b) => (a.type > b.type ? 1 : -1));
    },
  },
  totalBillAmount: {
    transform: ({ src }) => {
      // An EatApp reservation carries its bill on the booking itself, so the summary reads it
      // straight off `amount`. Its line items have no money on them at all, which is why
      // adding them up is not an option here. Every other venue keeps summing line items.
      if (isEatappSource(src.source)) {
        return src.amount;
      }
      if (src.bookingItems && src.bookingItems.length > 0) {
        var amount = 0;
        src.bookingItems.forEach((bookingItem) => {
          amount = amount + bookingItem.totalAmount;
        });
        return amount;
      } else {
        return 0;
      }
    },
  },
  currency: {
    // Line items don't always carry their own currency (depends on the PMS integration),
    // so fall back to the booking's own currency before giving up.
    transform: ({ src }) => {
      if (src.bookingItems && src.bookingItems.length > 0 && src.bookingItems[0].currency) {
        return src.bookingItems[0].currency;
      } else if (src.currency) {
        return src.currency;
      } else {
        return "";
      }
    },
  },
  bookedByCustomer: {
    transform: ({ src }) => {
      if (src.bookingCustomers) {
        const booker = src.bookingCustomers?.filter(function (e) {
          return e.isBooker;
        });
        if (booker && booker.length > 0) {
          return booker[0].customer;
        } else {
          const owner = src.bookingCustomers?.filter(function (e) {
            return e.isOwner;
          });
          if (owner && owner.length > 0) {
            return owner[0].customer;
          }
        }
      }
      return null;
    },
  },
  bookedBy: {
    transform: ({ src }) => {
      if (src.bookingCustomers) {
        const booker = src.bookingCustomers?.filter(function (e) {
          return e.isBooker;
        });
        if (booker && booker.length > 0) {
          var name = "";
          if (booker[0].customer.lastName) {
            name = booker[0].customer.lastName;
          }
          if (booker[0].customer.firstName) {
            name = booker[0].customer.firstName + " " + name;
          }
          return name;
        } else {
          const owner = src.bookingCustomers?.filter(function (e) {
            return e.isOwner;
          });
          if (owner && owner.length > 0) {
            var ownerName = "";
            if (owner[0].customer.lastName) {
              ownerName = owner[0].customer.lastName;
            }
            if (owner[0].customer.firstName) {
              ownerName = owner[0].customer.firstName + " " + ownerName;
            }
            return ownerName;
          }
        }
      }
      return null;
    },
  },
  bookedOn: {
    transform: ({ src }) => {
      return src.bookedOn ? formatDate(src.bookedOn, DateTime.D) : "-";
    },
  },
  company: {
    transform: ({ src }) => {
      return !src.company || src.company === "" ? "-" : src.company;
    },
  },
  mobileNumber: {
    transform: ({ src }) => {
      if (!src.countryCode || !src.mobileNumber) {
        return "-";
      }
      return `(${src.countryCode}) ${src.mobileNumber}`;
    },
  },
  notes: {
    transform: ({ src }) => {
      if (src.source === BookingSource.EATAPP) {
        return src.note ? [{ notes: src.note }] : [];
      }
      return src.bookingNotes;
    },
  },
  linkMiniWeb: {
    transform: ({ src }) => {
      return src.linkMiniWeb || "";
    },
  },
  // An EatApp reservation seats adults only, so its seat count is the adult count. Every other
  // venue seats children too and keeps counting both, as it did before.
  reservationGuestCount: {
    transform: ({ src }) => {
      return isEatappSource(src.source) ? src.adultCount : src.adultCount + src.childCount;
    },
  },
  // Package is carried on the guest profile assigned to the reservation's seats, not on
  // the booking itself.
  packageName: {
    transform: ({ src }) => {
      const descriptions = (src.bookingCustomers || [])
        .map((bookingCustomer) => (bookingCustomer.guestProfile?.description || "").trim())
        .filter(Boolean);
      return [...new Set(descriptions)].join(" · ");
    },
  },
  // The reservation returns a row for every seat, including seats whose guest has not
  // filled in the pre-checkin form yet, so the row count is not the completed count.
  // A submitted profile is one that carries an email, which is how the pre-checkin app
  // itself counts them (`hasSubmittedProfile` in its `booking-detail.mapper.ts`).
  filledGuests: {
    transform: ({ src }) => {
      return (src.bookingCustomers || []).filter((bookingCustomer) =>
        Boolean(bookingCustomer.customer?.email?.trim())
      ).length;
    },
  },
  // Booking a zone with ticket add-ons on top is not supported, so a reservation is
  // either a zone or a ticket, never both.
  reservationType: {
    transform: ({ src }) => {
      const itemTypes = new Set((src.bookingItems || []).map((bookingItem) => bookingItem.type));
      if (itemTypes.has(BookingItemType.PLACE)) {
        return BookingItemType.PLACE;
      }
      if (itemTypes.has(BookingItemType.TICKET)) {
        return BookingItemType.TICKET;
      }
      return null;
    },
  },
  zoneName: {
    transform: ({ src }) => {
      const assignedSpaces = (src.bookingItems || []).flatMap((bookingItem) =>
        (bookingItem.properties || [])
          .filter((property) => property.type === BookingItemPropertyType.ASSIGNED_SPACE)
          .map((property) => (property.value || "").trim())
          .filter(Boolean)
      );
      return [...new Set(assignedSpaces)].join(" · ");
    },
  },
  // A ticket reservation names its tickets on the line items themselves, the way a zone
  // reservation names its zone on the assigned-space property.
  ticketName: {
    transform: ({ src }) => {
      const ticketNames = (src.bookingItems || [])
        .filter((bookingItem) => bookingItem.type === BookingItemType.TICKET)
        .map((bookingItem) => (bookingItem.name || "").trim())
        .filter(Boolean);
      return [...new Set(ticketNames)].join(" · ");
    },
  },
  noOfNights: {
    transform: ({ src }) => {
      let count = 0;
      let endTime = src.endTime.split("T")[0];
      if (src.source === IntegrationType.MEWS && src.bookingItems) {
        let placeBookings = src.bookingItems.filter(
          (bookingItem) => BookingItemType.PLACE === bookingItem.type
        );
        if (placeBookings.length > 0) {
          count = placeBookings[0].quantity;
        }
        if (count > 0) {
          return count;
        }
      }

      return getNightsCount(src.startTime, endTime);
    },
  },
  reasonsForStay: { key: "reasonsForStay" },
  status: { key: "status" },
  integrationName: { key: "source" },
  customers: {
    transform: ({ src }) => {
      if (!src.bookingCustomers) {
        return [];
      }

      const mapBookingCustomer = (bookingCustomer) => {
        const { customer } = bookingCustomer;
        let name = "";
        if (customer.lastName) {
          name = customer.lastName;
        }
        if (customer.firstName) {
          name = customer.firstName + " " + name;
        }
        if (!name && bookingCustomer.tagUid) {
          name = bookingCustomer.tagUid + " (" + bookingCustomer.useCount + ")";
        }
        return {
          firstName: customer.firstName,
          lastName: customer.lastName,
          name: name,
          isOwner: bookingCustomer.isOwner,
          checkedInAt: bookingCustomer.checkedInAt,
          email: customer.email,
          mobileNumber: customer.mobileNumber,
          gender: gender2str(customer.gender),
          // Soar pre-checkin stores a title (Mr/Ms/Mrs) in the same field, which
          // gender2str does not map. Keep the raw value so the column can use it.
          genderRaw: customer.gender,
          birthdate: customer.birthdate,
          nationality: customer?.nationality,
          identificationType: identificationType2str(customer?.identificationType),
          idNumber: customer?.cardNumber,
          idScan: customer?.idScan,
          medicalCondition: customer?.medicalCondition,
          foodAllergies: customer?.foodAllergies,
          specialNotes: customer?.specialNotes,
          ticketNo: bookingCustomer?.externalTicketId,
          guestProfile: bookingCustomer.guestProfile?.guestProfileName,
          preloadedCredit: bookingCustomer?.preLoadedCredit,
          freeCredit: bookingCustomer?.preLoadedFreeCredit,
          staffNote: bookingCustomer?.reason,
        };
      };

      const owners = src.bookingCustomers
        .filter((bookingCustomer) => bookingCustomer.isOwner)
        .map(mapBookingCustomer);

      const others = src.bookingCustomers
        .filter((bookingCustomer) => {
          if (bookingCustomer.isOwner) {
            return false;
          }
          // On an EatApp reservation every seat is a real guest, the booker included, so the
          // booker is kept. Every other venue still drops it.
          return isEatappSource(src.source) ? true : !bookingCustomer.isBooker;
        })
        .map(mapBookingCustomer)
        .sort((a, b) => (a.name ? a.name.localeCompare(b.name) : 0));

      return owners.concat(others);
    },
  },
  accommodations: {
    transform: ({ src }) => {
      if (src.bookingItems) {
        return src.bookingItems.filter((bookingItem) => BookingItemType.PLACE === bookingItem.type);
      } else {
        return [];
      }
    },
  },
  products: {
    transform: ({ src }) => {
      if (src.bookingItems) {
        return src.bookingItems.filter(
          (bookingItem) => BookingItemType.PRODUCT === bookingItem.type
        );
      } else {
        return [];
      }
    },
  },
  isWarningUpdate: {
    transform: ({ src }) => {
      if (src?.source === IntegrationType.CLOUDBEDS) {
        if (src.bookingCustomers.length === 1) {
          return !src.bookingCustomers[0].cloudbedsRoomId;
        } else
          return (
            src.bookingCustomers.findIndex((c) => !c.isBooker && isEmpty(c?.cloudbedsRoomId)) > -1
          );
      }
      return false;
    },
  },
};

export const bookingSummaryResponse = {
  items: { key: "items" },
  paymentMethod: { key: "paymentMethod" },
  paymentStatus: { key: "paymentStatus" },
  totalBillAmount: { key: "totalBillAmount" },
};

// export const bookingDetailsHeader = {
//   bookingId: { key: "bookingId" },
//   bookedBy: { key: "bookedBy" },
//   bookedOn: { key: "bookedOn" },
//   integrationName: { key: "integrationName" },
//   status: {
//     transform: ({ src }) => {
//       return "CHECKED_IN";
//     },
//   },
// };

export const bookingListResponse = {
  id: { key: "id" },
  bookingNumber: { key: "bookingNumber" },
  source: {
    transform: ({ src }) => {
      return src.source;
    },
  },
  startTime: {
    transform: ({ src }) => {
      if (src.source === IntegrationType.CLOUDBEDS) {
        return src.startTime.split("T")[0];
      } else {
        return src.startTime;
      }
    },
  },
  endTime: {
    transform: ({ src }) => {
      if (src.source === IntegrationType.CLOUDBEDS) {
        return src.endTime.split("T")[0];
      } else {
        return src.endTime;
      }
    },
  },
  bookedBy: {
    transform: ({ src }) => {
      if (src.bookingCustomers) {
        const booker = src.bookingCustomers?.filter(function (e) {
          return e.isBooker;
        });
        if (booker && booker.length > 0) {
          var name = "";
          if (booker[0].customer.lastName) {
            name = booker[0].customer.lastName;
          }
          if (booker[0].customer.firstName) {
            name = booker[0].customer.firstName + " " + name;
          }
          return name;
        } else {
          const owner = src.bookingCustomers?.filter(function (e) {
            return e.isOwner;
          });
          if (owner && owner.length > 0) {
            var ownerName = "";
            if (owner[0].customer.lastName) {
              ownerName = owner[0].customer.lastName;
            }
            if (owner[0].customer.firstName) {
              ownerName = owner[0].customer.firstName + " " + ownerName;
            }
            return ownerName;
          }
        }
      }
      return "";
    },
  },
  emailAddress: {
    transform: ({ src }) => {
      if (src.bookingCustomers) {
        const booker = src.bookingCustomers?.filter(function (e) {
          return e.isBooker;
        });
        if (booker && booker.length > 0) {
          return booker[0].customer.email;
        } else {
          const owner = src.bookingCustomers?.filter(function (e) {
            return e.isOwner;
          });
          if (owner && owner.length > 0) {
            return owner[0].customer.email;
          }
        }
      }
      return "";
    },
  },
  noOfGuests: {
    transform: ({ src }) => {
      return src.adultCount + src.childCount;
    },
  },
  noOfNights: {
    transform: ({ src }) => {
      let count = 0;
      if (src.source === IntegrationType.MEWS && src.bookingItems) {
        let placeBookings = src.bookingItems.filter(
          (bookingItem) => BookingItemType.PLACE === bookingItem.type
        );
        if (placeBookings.length > 0) {
          count = placeBookings[0].quantity;
        }
        if (count > 0) {
          return count;
        }
      }

      return getNightsCount(src.startTime, src.endTime);
    },
  },
  status: { key: "status" },
  rawStatus: { key: "rawStatus" },
  noOfCheckedIn: { key: "checkedInGuest" },
  noOfCheckedOut: { key: "checkedOutGuest" },
};

export const bookingListFilterRequest = {
  ...filterRequest,
  ...eventBookingDateTimeRequest,
  status: selectToObjectRequest("statusList"),
  source: selectToObjectRequest("sourceList"),
  orders: {
    endTime: "DESC",
  },
};

export const bookingExportRequest = {
  ...filterRequest,
  ...eventBookingDateTimeRequest,
  status: selectToObjectRequest("statusList"),
  source: ["IMPORT"],
};
